// Real backend integration for My Profile (FEATURE_052/053).
// Maps the server error envelope { error: { code, message, fields, meta } } to MyProfileApiError.
// Used when VITE_USE_MOCK_API=false (docker/prod); see my-profile.adapter.ts.

import { signInWithCustomToken } from 'firebase/auth';

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import { auth } from '@/api/firebase';
import {
  MyProfileApiError,
  type MyProfile,
  type MyProfileApi,
  type MyProfileApiErrorCode,
  type MyProfileApiErrorMeta,
  type PasswordChangeInput,
  type PasswordChangeResult,
  type PhoneSaveInput,
  type PhoneSendCodeInput,
} from './my-profile.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new MyProfileApiError(
    (payload?.code as MyProfileApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    {
      fields: payload?.fields,
      meta: payload?.meta as MyProfileApiErrorMeta | undefined,
    },
  );
}

export const myProfileHttpApi: MyProfileApi = {
  // GET /api/v1/admin/my-profile — read-only 3-card profile (FEATURE_052).
  async getMe(): Promise<MyProfile> {
    try {
      const res = await httpClient.get<ApiEnvelope<MyProfile>>(endpoints.auth.myProfile);
      // Backend contract is `{ data: MyProfile }`. A null/undefined body (wrong
      // envelope, SPA fallback, …) must surface as a load error, not a downstream
      // crash reading profile fields off `undefined`.
      if (res.data?.data == null) {
        throw new MyProfileApiError('UNKNOWN', '알 수 없는 오류가 발생했습니다.');
      }
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  // POST .../phone/request-otp — send the SMS code (backend field: `phone`). 204, no body.
  async sendPhoneCode(input: PhoneSendCodeInput): Promise<void> {
    try {
      await httpClient.post(endpoints.auth.myProfilePhoneRequestOtp, {
        phone: input.newPhone,
      });
    } catch (e) {
      mapError(e);
    }
  },

  // POST .../phone/verify — verify the code AND commit the new number; returns the updated profile.
  async savePhone(input: PhoneSaveInput): Promise<MyProfile> {
    try {
      const res = await httpClient.post<ApiEnvelope<MyProfile>>(
        endpoints.auth.myProfilePhoneVerify,
        { phone: input.newPhone, code: input.code },
      );
      // Contract is `{ data: MyProfile }`; a null body would white-screen the
      // page when it re-renders the updated profile. Surface it as an error.
      if (res.data?.data == null) {
        throw new MyProfileApiError('UNKNOWN', '알 수 없는 오류가 발생했습니다.');
      }
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  // POST .../password — change password (FEATURE_053, session continues). Changing the Firebase password
  // invalidates the FE's current ID token, so the backend returns a fresh custom token. Exchange it via
  // signInWithCustomToken BEFORE returning — the caller then refetches /my-profile, and without a valid token
  // that GET would 401 and the interceptor would force a logout. Mirrors the login exchange (api/auth.ts) and
  // the medical-web my-profile adapter.
  async changePassword(input: PasswordChangeInput): Promise<void> {
    try {
      const res = await httpClient.post<ApiEnvelope<PasswordChangeResult>>(
        endpoints.auth.myProfilePassword,
        {
          currentPassword: input.currentPassword,
          newPassword: input.newPassword,
          confirmPassword: input.confirmPassword,
        },
      );
      const accessToken = res.data?.data?.accessToken;
      if (accessToken) {
        await signInWithCustomToken(auth, accessToken);
      }
    } catch (e) {
      mapError(e);
    }
  },
};
